import React, { useState, useMemo } from 'react';
import { CODEGEN_TABLES as T } from '../codegenData';
import { useRegion } from '../region';

// Reproduces the 'Alterations Code Generator' spreadsheet:
// CODE = subcat-{series3}{heads}{shape}{size}-{watt}{chip}-{cri}{cct}-{beam}{optic}-{finish1}{bezel}-{access}{dimming}
// plus Sales & Purchase descriptions (TEXTJOIN of the human-readable values).
const codeOf = (table, desc) => { const r = (T[table] || []).find(o => o.desc === desc); return r ? r.code : ''; };

// ---- reverse mapping: datasheet -> generator fields ----
const norm = (x) => (x == null ? '' : String(x)).trim().toLowerCase();
const firstLine = (x) => (x == null ? '' : String(x)).split(/\r?\n/)[0].trim();
const digits = (x) => { const m = String(x == null ? '' : x).replace(/,/g, '').match(/\d+(\.\d+)?/); return m ? m[0] : ''; };

function matchDesc(table, value) {
  const v = norm(value);
  if (!v) return '';
  const opts = T[table] || [];
  let hit = opts.find(o => norm(o.desc) === v);
  if (!hit) hit = opts.find(o => norm(o.desc) && (v.includes(norm(o.desc)) || norm(o.desc).includes(v)));
  return hit ? hit.desc : '';
}

function keywordDesc(table, text) {
  const t = norm(text);
  if (!t) return '';
  const opts = [...(T[table] || [])].sort((a, b) => (b.desc || '').length - (a.desc || '').length);
  const hit = opts.find(o => o.desc && t.includes(norm(o.desc)));
  return hit ? hit.desc : '';
}

// Reads a filled datasheet and derives every generator field it can.
export function fieldsFromSheet(s) {
  s = s || {};
  const cs = s.commonSpecs || {};
  const pd = s.productDetails || {};
  const v0 = (s.variants || [])[0] || {};
  const nameText = [s.name, s.variant].filter(Boolean).join(' ');
  const b = s.badges || {};
  const badgeKeys = Object.keys(b).filter(k => b[k]);
  const ipBadge = badgeKeys.find(k => /^IP\d+$/i.test(k));
  const ikBadge = badgeKeys.find(k => /^IK\d+$/i.test(k));
  const criBadge = badgeKeys.find(k => /^CRI\d+$/i.test(k));
  const beamLumens = Object.values(v0.beamLumens || {}).map(digits).filter(Boolean);
  const beamCol = (s.beamCols || [])[0] || {};
  const out = {
    series: (s.name || '').trim(),
    subCategory: matchDesc('revSubCategory', s.variant) || keywordDesc('revSubCategory', nameText),
    size: keywordDesc('size', nameText),
    shape: keywordDesc('shape', nameText),
    heads: keywordDesc('heads', nameText),
    trim: norm(nameText).includes('trimless') ? 'Trimless' : '',
    systemWattage: digits(v0.systemPower),
    systemLumen: beamLumens[0] || digits(pd.lightOutput),
    sourceLumen: digits(v0.sourceLumens),
    cri: matchDesc('cri', 'CRI ' + digits(firstLine(cs.cri))) || (criBadge ? matchDesc('cri', 'CRI ' + digits(criBadge)) : ''),
    cct: matchDesc('cct', firstLine(cs.cct)),
    ip: ipBadge ? matchDesc('ip', ipBadge) : '',
    ik: ikBadge ? matchDesc('ik', ikBadge) : '',
    optical: matchDesc('optical', firstLine(pd.optic)),
    beam: digits(beamCol.angle),
    finish: matchDesc('finish', firstLine(cs.colour)),
    bezel: matchDesc('bezel', firstLine(cs.bezel)),
    dimming: matchDesc('dimming', firstLine(cs.dimming)),
  };
  Object.keys(out).forEach(k => { if (!out[k]) delete out[k]; });
  return out;
}

const SELECTS = [
  ['subCategory', 'Sub Category', 'revSubCategory'],
  ['size', 'Size', 'size'],
  ['shape', 'Shape (optional)', 'shape'],
  ['heads', 'No. of Heads', 'heads'],
  ['trim', 'Trim', 'trim'],
  ['chip', 'Chip', 'chip'],
  ['cri', 'CRI', 'cri'],
  ['cct', 'CCT', 'cct'],
  ['ip', 'IP', 'ip'],
  ['ik', 'IK', 'ik'],
  ['optical', 'Optical System', 'optical'],
  ['finish', 'Finish Colour / Outer Trim', 'finish'],
  ['bezel', 'Bezel Colour', 'bezel'],
  ['accessories', 'Accessories', 'accessories'],
  ['dimming', 'Dimming', 'dimming'],
];

export default function CodeGenerator({ sheet, setSheet, toast, goEditor }) {
  const region = useRegion();
  const [f, setF] = useState({
    series: '', subCategory: '', size: '', shape: '', heads: '', trim: '',
    systemLumen: '', sourceLumen: '', systemWattage: '', chip: '', cri: '', cct: '',
    ip: '', ik: '', optical: '', beam: '', finish: '', bezel: '', accessories: '', dimming: '', driver: '',
  });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  const out = useMemo(() => {
    const series3 = (f.series || '').trim().slice(0, 3).toUpperCase();
    const seg1 = codeOf('revSubCategory', f.subCategory);
    const seg2 = series3 + codeOf('heads', f.heads) + codeOf('shape', f.shape) + codeOf('size', f.size);
    const seg3 = (f.systemWattage || '') + codeOf('chip', f.chip);
    const seg4 = codeOf('cri', f.cri) + codeOf('cct', f.cct);
    const seg5 = (f.beam || '') + codeOf('optical', f.optical);
    const finish1 = (codeOf('finish', f.finish) || '').slice(0, 1); // spreadsheet uses 1st char of finish code
    const seg6 = finish1 + codeOf('bezel', f.bezel);
    const accCode = codeOf('accessories', f.accessories);
    const dimCode = codeOf('dimming', f.dimming);
    // No accessory chosen: "NA" placeholder only when dimming is present, otherwise blank.
    const seg7 = (accCode || (dimCode ? 'NA' : '')) + dimCode;
    const code = [seg1, seg2, seg3, seg4, seg5, seg6, seg7].join('-');

    const join = (arr) => arr.map(x => (x == null ? '' : String(x).trim())).filter(Boolean).join(', ');
    const sales = join([
      f.series, f.subCategory, f.size, f.shape, f.heads, f.trim,
      f.systemLumen && f.systemLumen + 'Lm', f.systemWattage && f.systemWattage + 'W',
      f.cri, f.cct, f.ip, f.ik, f.beam && f.beam + ' Degree', f.optical,
      f.finish, f.bezel, f.accessories, f.dimming,
    ]);
    const purchase = join([
      f.series, f.subCategory, f.size, f.shape, f.heads, f.trim,
      f.sourceLumen && f.sourceLumen + ' Source Lm', f.systemLumen && f.systemLumen + ' System Lm',
      f.systemWattage && f.systemWattage + 'W', f.cri, f.cct, f.cct, f.ip, f.ik,
      f.beam && f.beam + ' Degree', f.finish, f.bezel, f.accessories, f.driver,
    ]);
    return { code, sales, purchase };
  }, [f]);

  function copy(v) { if (v && navigator.clipboard) navigator.clipboard.writeText(v).then(() => toast('Copied'), () => {}); }

  function fillFromSheet() {
    const m = fieldsFromSheet(sheet);
    const n = Object.keys(m).length;
    if (!n) { toast('The datasheet in the Editor is empty — fill it in first', true); return; }
    setF(s => ({ ...s, ...m }));
    const manual = ['chip', 'accessories'].filter(k => !m[k]);
    toast('Filled ' + n + ' field' + (n === 1 ? '' : 's') + ' from “' + ((sheet && sheet.name) || 'datasheet') + '”' + (manual.length ? ' — set ' + manual.join(' & ') + ' manually' : ''));
  }

  if (region === 'US') {
    return (
      <div className="codegen-page" data-testid="codegen-page">
        <div className="codegen-form" data-testid="codegen-us-placeholder" style={{ textAlign: 'center', padding: '80px 24px' }}>
                    <h2 style={{ color: 'var(--brand-navy)' }}>US Product Code Generator</h2>
          <p className="codegen-sub" style={{ maxWidth: 460, margin: '10px auto 0' }}>
            The US coding system is coming soon. The US uses a different product-code format
            which will be configured here once the rules are finalised.
          </p>
          <p className="field-hint" style={{ marginTop: 18 }}>Switch to AU Mode to use the current LPA code generator.</p>
        </div>
      </div>
    );
  }

  function applyToDatasheet() {
    setSheet(s => ({
      ...s,
      name: f.series || s.name,
      variant: f.subCategory || s.variant,
      code: out.code || s.code,
      productDetails: {
        ...s.productDetails,
        lightOutput: f.systemLumen ? f.systemLumen + 'lm' : (s.productDetails || {}).lightOutput,
        optic: f.optical || (s.productDetails || {}).optic,
      },
      commonSpecs: {
        ...s.commonSpecs,
        cri: f.cri || (s.commonSpecs || {}).cri,
        cct: f.cct || (s.commonSpecs || {}).cct,
        dimming: f.dimming || (s.commonSpecs || {}).dimming,
        colour: f.finish || (s.commonSpecs || {}).colour,
        bezel: f.bezel || (s.commonSpecs || {}).bezel,
      },
      salesDescription: out.sales,
      purchaseDescription: out.purchase,
    }));
    toast('Applied to datasheet');
    goEditor && goEditor();
  }

  return (
    <div className="codegen-page" data-testid="codegen-page">
      <div className="codegen-form">
        <h2>Product Code Generator</h2>
        <p className="codegen-sub">Build an LPA product code, sales &amp; purchase descriptions using the official coding format.</p>

        <div className="cg-fill-bar">
          <button className="btn" data-testid="cg-fill-from-sheet" onClick={fillFromSheet}>
            Fill from current datasheet{sheet && sheet.name ? ' — ' + sheet.name : ''}
          </button>
          <span className="field-hint" style={{ margin: 0 }}>Works in reverse too: reads the datasheet open in the Editor and pre-fills everything it can (chip &amp; accessories aren't on the datasheet, so pick those manually).</span>
        </div>

        <div className="cg-grid">
          <CGField label="Series / Family"><input className="input" data-testid="cg-series" value={f.series} onChange={e => set('series', e.target.value)} placeholder="e.g. Parker" /></CGField>
          {SELECTS.map(([k, label, table]) => (
            <CGField key={k} label={label}>
              <select className="input" data-testid={'cg-' + k} value={f[k]} onChange={e => set(k, e.target.value)}>
                <option value="">—</option>
                {(T[table] || []).map((o, i) => <option key={i} value={o.desc}>{o.desc}{o.code ? ` (${o.code})` : ''}</option>)}
              </select>
            </CGField>
          ))}
          <CGField label="System Wattage (W)"><input className="input" data-testid="cg-systemWattage" value={f.systemWattage} onChange={e => set('systemWattage', e.target.value)} placeholder="e.g. 8" /></CGField>
          <CGField label="Beam Angle"><input className="input" data-testid="cg-beam" value={f.beam} onChange={e => set('beam', e.target.value)} placeholder="e.g. 25" /></CGField>
          <CGField label="System Lumen"><input className="input" value={f.systemLumen} onChange={e => set('systemLumen', e.target.value)} placeholder="e.g. 2513" /></CGField>
          <CGField label="Source Lumen"><input className="input" value={f.sourceLumen} onChange={e => set('sourceLumen', e.target.value)} placeholder="e.g. 3000" /></CGField>
          <CGField label="Driver Part Number"><input className="input" value={f.driver} onChange={e => set('driver', e.target.value)} placeholder="e.g. Philips 500mA" /></CGField>
        </div>
      </div>

      <aside className="codegen-out">
        <div className="cg-result">
          <label>Product Code</label>
          <div className="cg-code" data-testid="cg-code">{out.code}</div>
          <button className="btn btn-sm" onClick={() => copy(out.code)}>Copy code</button>
        </div>
        <div className="cg-result">
          <label>Sales Description</label>
          <div className="cg-desc" data-testid="cg-sales">{out.sales || '—'}</div>
          <button className="btn btn-sm" onClick={() => copy(out.sales)}>Copy</button>
        </div>
        <div className="cg-result">
          <label>Purchase Description</label>
          <div className="cg-desc" data-testid="cg-purchase">{out.purchase || '—'}</div>
          <button className="btn btn-sm" onClick={() => copy(out.purchase)}>Copy</button>
        </div>
        <button className="btn btn-primary cg-apply" data-testid="cg-apply" onClick={applyToDatasheet}>Apply to current datasheet →</button>
      </aside>
    </div>
  );
}

function CGField({ label, children }) {
  return <div className="field"><label className="field-label">{label}</label>{children}</div>;
}
